import { useEffect } from "react";

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE = "https://hdlyjgs.cn";

function upsertTag(selector: string, create: () => HTMLElement, attrs: Record<string, string>) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el?.setAttribute(key, value));
  return el;
}

export function useSeo({ title, description, keywords, path, jsonLd }: SeoOptions) {
  useEffect(() => {
    document.title = title;

    const canonical = `${SITE}${path}`;

    upsertTag(
      'meta[name="description"]',
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      },
      { name: "description", content: description },
    );

    if (keywords) {
      upsertTag(
        'meta[name="keywords"]',
        () => {
          const m = document.createElement("meta");
          m.setAttribute("name", "keywords");
          return m;
        },
        { name: "keywords", content: keywords },
      );
    }

    upsertTag(
      'meta[name="robots"]',
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "robots");
        return m;
      },
      { name: "robots", content: "index,follow,max-image-preview:large" },
    );

    upsertTag(
      'link[rel="canonical"]',
      () => document.createElement("link"),
      { rel: "canonical", href: canonical },
    );

    const ogTags: Record<string, string> = {
      "og:title": title,
      "og:description": description,
      "og:url": canonical,
      "og:type": "website",
      "og:site_name": "拾真社",
      "og:locale": "zh_CN",
    };
    Object.entries(ogTags).forEach(([key, value]) => {
      upsertTag(
        `meta[property="${key}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute("property", key);
          return m;
        },
        { property: key, content: value },
      );
    });

    const twitterTags: Record<string, string> = {
      "twitter:card": "summary_large_image",
      "twitter:title": title,
      "twitter:description": description,
    };
    Object.entries(twitterTags).forEach(([key, value]) => {
      upsertTag(
        `meta[name="${key}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute("name", key);
          return m;
        },
        { name: key, content: value },
      );
    });

    let script: HTMLScriptElement | null = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, path, jsonLd]);
}